import {UHttp} from "../../utils/UHttp"

export interface SirenVerificationResponse {
    success: boolean;
    isValid: boolean;
    data: any;
    professionalUpdated?: boolean;
    message?: string;
    status?: number;
}

export interface ProfileResponse {
    success: boolean,
    message?: string,
    status: number,
    profile?: Profile
}

// Types
export interface Permission {
    id: string;
    name: string;
}

export interface Role {
    id: string;
    name: string;
}

/**
 * Interface de permissions
 */
export interface PermissionsRequest {
    userId: string;
    permissionId: string;
    companyId?: string;
}

export interface CollaboratorData {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    picture: string | null;
}

export interface CollaboratorEditViewData {
    collaborator: CollaboratorData;
    role: Role & { description?: string };
    collaboratorCompanyId: string;
    permissions: Permission[];
}

export interface AvailableRolesAndPermissions {
    roles: Role[];
    permissions: Permission[];
}

export type PaymentMethod =
  | 'Cash'
  | 'Bank Transfer'
  | 'Credit Card'
  | 'Check'
  | 'PayPal'
  | 'Mobile Payment';

export interface UpdateProfilCredentials {
    firstName?: string,
    lastName?: string,
    phone?: string,
    profession?: string;
    timeZone?: string;
    language?: string;
    onboarding?: boolean;
}

export interface UpdateSecurityCredentials {
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
}

export interface UpdateOnboardingCredentials {
    onboarding: boolean,
}

export interface UpdateSecurityResponse {
    success: boolean;
    message: string;
    status: number;
}

export interface UpdateProfileResponse {
    success: boolean;
    message: string;
    status: number;
}


interface Profile {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    emailVerified: boolean;
    siren: string;
    sirenVerified: boolean;
    profession: string;
    timeZone: string;
    language: string;
    onboarding?: boolean;
    picture?: string | null;
    stripeConnectedAccountId?: string | null;
    stripeAccountStatus?: 'not_started' | 'pending' | 'verified' | 'restricted';
    absorbStripeFees?: boolean;
}


const apiUrlServer = process.env.REACT_APP_URL_SERVER;


export class ProfessionalModel {
    private static instance: ProfessionalModel;

    private constructor() {}

    public static getInstance(): ProfessionalModel {
        if (!ProfessionalModel.instance) {
            ProfessionalModel.instance = new ProfessionalModel();
        }
        return ProfessionalModel.instance;
    }

    public async handleGetProfil (): Promise<ProfileResponse> {
        try {
            const response = await UHttp.get(
                apiUrlServer + "/api/collaborator/profile",
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetProfil:', error.data);
            throw error;
        }
    }

    public async handleGetDataCollaboratorByCompany (companyId: string, collaboratorId: string): Promise<CollaboratorEditViewData> {
        try {
            const response = await UHttp.get(
                apiUrlServer + `/api/collaborator/company/${companyId}/collaborator/${collaboratorId}/details`,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            // Extraire les données de la réponse API
            return responseData.data;

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetDataCollaboratorByCompany:', error.data);
            throw error;
        }
    }

    public async handleGetDataRolesAndPermissions (): Promise<AvailableRolesAndPermissions> {
        try {
            const response = await UHttp.get(
                apiUrlServer + `/api/role-permission/all`,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            // Extraire les données de la réponse API
            return responseData.data;

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetDataRolesAndPermissions:', error.data);
            throw error;
        }
    }

    public async handleRevokePermissions (data: PermissionsRequest[]): Promise<any> {
        try {
            const response = await UHttp.post(
                apiUrlServer + `/api/collaborator/admin/permissions/revoke`,
                {permissions: data},
                UHttp.authHeaders()
            )

            // Extraire les données de la réponse API
            return await response.json();

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetDataRolesAndPermissions:', error.data);
            throw error;
        }
    }

    public async handleGrantPermissions (data: PermissionsRequest[]): Promise<any> {
        try {
            const response = await UHttp.post(
                apiUrlServer + `/api/collaborator/admin/permissions/grant`,
                {permissions: data},
                UHttp.authHeaders()
            )

            // Extraire les données de la réponse API
            return await response.json();

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetDataRolesAndPermissions:', error.data);
            throw error;
        }
    }

    public async handleAddCollaborator (data: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        phone: string;
        companyId: string;
        roleId: string;
        permissionIds: string[];
    }): Promise<{success: boolean, message: string, collaborator: any, status: number}> {
        try {
            const response = await UHttp.post(
                apiUrlServer + `/api/collaborator/admin/collaborator`,
                data,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleAddCollaborator:', error);
            throw error;
        }
    }

    public async handleDeleteCollaborator (companyId: string, collaboratorCompanyId: string): Promise<{message: string, status: number, success: boolean}> {
        try {
            const response = await UHttp.delete(
                apiUrlServer + `/api/collaborator/admin/company/${companyId}/collaborator/${collaboratorCompanyId}`,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleDeleteCollaborator:', error);
            throw error;
        }
    }

    public async handleUpdateProfil (credentials: UpdateProfilCredentials): Promise<UpdateSecurityResponse> {
        try {
            const response = await UHttp.put(
                apiUrlServer + "/api/collaborator/profile",
                credentials,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            throw error;
        }
    }

    public async handleUpdateSecurity (credentials: UpdateSecurityCredentials): Promise<UpdateProfileResponse> {
        try {
            const response = await UHttp.put(
                apiUrlServer + "/api/auth/change-password",
                credentials,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleGetProfil:', error.data);
            throw error;
        }
    }

    public async handleUpdateOnboarding (credentials: UpdateOnboardingCredentials): Promise<UpdateProfileResponse> {
        try {
            const serverPayload = {
                onboardingStatus: credentials.onboarding
            };
            
            const response = await UHttp.put(
                apiUrlServer + "/api/collaborator/onboarding",
                serverPayload,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.handleUpdateOnboarding:', error.data);
            throw error;
        }
    }

    /**
     * Upload la photo de profil du collaborateur
     * @param file Fichier image à uploader
     * @returns Réponse avec l'URL de l'image uploadée
     */
    public async uploadProfilePicture(file: File): Promise<{success: boolean, message: string, url?: string, status: number}> {
        try {
            const formData = new FormData();
            formData.append('profilePicture', file);

            const response = await UHttp.putFormData(
                apiUrlServer + "/api/collaborator/profile-picture",
                formData,
                UHttp.authHeaders()
            );

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.uploadProfilePicture:', error);
            throw error;
        }
    }

    /**
     * Met à jour le paramètre absorbStripeFees de l'Owner
     * @param absorbStripeFees Nouvelle valeur du paramètre
     * @returns Réponse avec le statut de la mise à jour
     */
    public async updateAbsorbStripeFees(absorbStripeFees: boolean): Promise<{success: boolean, message: string, status: number}> {
        try {
            const response = await UHttp.put(
                apiUrlServer + "/api/collaborator/absorb-stripe-fees",
                { absorbStripeFees },
                UHttp.authHeaders()
            );

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.updateAbsorbStripeFees:', error);
            throw error;
        }
    }

    /**
     * Supprime le compte de l'utilisateur et toutes ses données associées
     * Pour un owner: supprime companies, abonnements, Stripe Connect, etc.
     * Pour un collaborateur: supprime uniquement son profil
     */
    public async deleteAccount(): Promise<{success: boolean, message: string, status: number}> {
        try {
            const response = await UHttp.delete(
                apiUrlServer + "/api/collaborator/delete-account",
                UHttp.authHeaders()
            );

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error ProfessionalModel.deleteAccount:', error);
            throw error;
        }
    }
}
export default ProfessionalModel.getInstance()